import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import MehrsanWorkQueueApi from "../../../../../infrastructure/api/mehrsanWorkQueue";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";
import { getCurrentUserId } from "../../../../components/request/requestAssignmentDisplay";
import { toJalaliFullDate } from "../../../../middleware/middleware";
import MehrsanWorkQueueCaseDrawer from "./MehrsanWorkQueueCaseDrawer";
import MehrsanPortalExcelImportDialog from "./MehrsanPortalExcelImportDialog";

const PAGE_TITLE = "میز کار مهرسان";
const OPERATIONS_SOURCE = "SolarentOperator";

export const QUEUE_TABS = [
  { key: "registration", label: "ثبت مهرسان", countField: "registrationCount" },
  { key: "inProgress", label: "مرحله جاری", countField: "inProgressCount" },
  { key: "completed", label: "تکمیل شده", countField: "completedCount" },
];

const pick = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal] ?? null;

export const normalizeQueueRow = (raw) => ({
  requestInfoId: pick(raw, "requestInfoId", "RequestInfoId"),
  requestTrackingCode: pick(raw, "requestTrackingCode", "RequestTrackingCode"),
  applicantFullName: pick(raw, "applicantFullName", "ApplicantFullName") || "—",
  applicantMobile: pick(raw, "applicantMobile", "ApplicantMobile") || "",
  plantCapacityKw: pick(raw, "plantCapacityKw", "PlantCapacityKw"),
  requestStepTitle: pick(raw, "requestStepTitle", "RequestStepTitle") || "",
  queueTab: pick(raw, "queueTab", "QueueTab") || "registration",
  currentStepId: pick(raw, "currentStepId", "CurrentStepId"),
  currentStepTitle: pick(raw, "currentStepTitle", "CurrentStepTitle") || "",
  completedStepCount: pick(raw, "completedStepCount", "CompletedStepCount") ?? 0,
  totalStepCount: pick(raw, "totalStepCount", "TotalStepCount") ?? 4,
  assignedToUserId: pick(raw, "assignedToUserId", "AssignedToUserId"),
  assignedToFullName: pick(raw, "assignedToFullName", "AssignedToFullName") || "",
  assignedAt: pick(raw, "assignedAt", "AssignedAt"),
  enteredMehrsanAt: pick(raw, "enteredMehrsanAt", "EnteredMehrsanAt"),
  lastActionAt: pick(raw, "lastActionAt", "LastActionAt"),
  openDays: pick(raw, "openDays", "OpenDays") ?? 0,
});

const MehrsanWorkQueuePage = () => {
  const currentUserId = getCurrentUserId();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({
    registrationCount: 0,
    inProgressCount: 0,
    completedCount: 0,
  });
  const [activeTab, setActiveTab] = useState("registration");
  const [experts, setExperts] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [assignDialog, setAssignDialog] = useState({ open: false, row: null, userId: "" });
  const [detailDrawer, setDetailDrawer] = useState({
    open: false,
    row: null,
    autoOpenNext: false,
  });
  const [busyRequestId, setBusyRequestId] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const loadFilterData = useCallback(async () => {
    try {
      const res = await MehrsanWorkQueueApi.getFilterData(OPERATIONS_SOURCE);
      const data = res?.data ?? res;
      const list = data?.experts ?? data?.Experts ?? [];
      setExperts(Array.isArray(list) ? list : []);
    } catch {
      setExperts([]);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {};
      if (appliedSearch.trim()) payload.searchText = appliedSearch.trim();
      if (assigneeFilter !== "") payload.assignedToUserId = Number(assigneeFilter);

      const res = await MehrsanWorkQueueApi.search(OPERATIONS_SOURCE, payload);
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا در دریافت صف کار مهرسان");
        setRows([]);
        setCounts({ registrationCount: 0, inProgressCount: 0, completedCount: 0 });
        return;
      }

      const data = res?.data ?? res;
      const list = data?.rows ?? data?.Rows ?? [];
      setRows(Array.isArray(list) ? list.map(normalizeQueueRow) : []);
      setCounts({
        registrationCount: data?.registrationCount ?? data?.RegistrationCount ?? 0,
        inProgressCount: data?.inProgressCount ?? data?.InProgressCount ?? 0,
        completedCount: data?.completedCount ?? data?.CompletedCount ?? 0,
      });
    } catch (e) {
      toast.error(e?.message || "خطا در دریافت صف کار مهرسان");
      setRows([]);
      setCounts({ registrationCount: 0, inProgressCount: 0, completedCount: 0 });
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, assigneeFilter]);

  useEffect(() => {
    loadFilterData();
  }, [loadFilterData]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleAssign = useCallback(
    async (row, assignedToUserId) => {
      if (!row?.requestInfoId) return;
      setBusyRequestId(row.requestInfoId);
      try {
        const res = await MehrsanWorkQueueApi.assign(
          OPERATIONS_SOURCE,
          row.requestInfoId,
          assignedToUserId || null
        );
        if (res?.isSuccess === false) {
          toast.error(res?.message || "خطا در ثبت مسئول پیگیری");
          return;
        }
        toast.success(res?.message || "مسئول پیگیری مهرسان ثبت شد");
        await loadList();
      } catch (e) {
        toast.error(e?.message || "خطا در ثبت مسئول پیگیری");
      } finally {
        setBusyRequestId(null);
      }
    },
    [loadList]
  );

  const openDetails = useCallback((row, autoOpenNext = false) => {
    setDetailDrawer({
      open: true,
      row,
      autoOpenNext: Boolean(autoOpenNext),
    });
  }, []);

  const tabRows = useMemo(
    () => rows.filter((r) => r.queueTab === activeTab),
    [rows, activeTab]
  );

  const tableRows = useMemo(
    () =>
      tabRows.map((row, index) => ({
        ...row,
        row_index: index + 1,
        capacityDisplay: row.plantCapacityKw ? `${row.plantCapacityKw} kW` : "—",
        stepProgressDisplay:
          row.queueTab === "completed"
            ? `${row.totalStepCount}/${row.totalStepCount}`
            : `${row.completedStepCount}/${row.totalStepCount}`,
        currentStepDisplay:
          row.queueTab === "completed"
            ? "تکمیل شده"
            : row.currentStepTitle || "—",
        assigneeDisplay: row.assignedToFullName || "بدون مسئول",
        assigneeColor: row.assignedToFullName
          ? Number(row.assignedToUserId) === Number(currentUserId)
            ? "success"
            : "info"
          : "default",
        lastActionDisplay: row.lastActionAt ? toJalaliFullDate(row.lastActionAt) : "—",
        openDaysDisplay:
          row.queueTab === "completed" ? "—" : `${row.openDays} روز`,
      })),
    [tabRows, currentUserId]
  );

  const headCells = useMemo(
    () => [
      { id: "row_index", label: "ردیف" },
      { id: "requestTrackingCode", label: "کد پیگیری" },
      { id: "applicantFullName", label: "متقاضی" },
      { id: "applicantMobile", label: "موبایل" },
      { id: "capacityDisplay", label: "ظرفیت" },
      { id: "currentStepDisplay", label: "مرحله فعلی" },
      { id: "stepProgressDisplay", label: "پیشرفت" },
      {
        id: "assigneeDisplay",
        label: "مسئول پیگیری",
        type: "chip",
        model: { colorField: "assigneeColor" },
      },
      { id: "lastActionDisplay", label: "آخرین اقدام" },
      { id: "openDaysDisplay", label: "روزهای باز" },
    ],
    []
  );

  const tableActions = useMemo(
    () => [
      {
        icon: <VisibilityIcon fontSize="small" />,
        onClick: (row) => openDetails(row, false),
        color: "primary",
        tooltip: "جزئیات و ثبت مرحله مهرسان",
      },
      {
        icon: <SkipNextIcon fontSize="small" />,
        onClick: (row) => openDetails(row, true),
        color: "warning",
        tooltip: "مرحله بعد مهرسان",
        hidden: (row) => row.queueTab === "completed",
      },
      {
        icon: <AssignmentIndIcon fontSize="small" />,
        onClick: (row) => handleAssign(row, null),
        color: "success",
        tooltip: "پیگیری با من",
        hidden: (row) =>
          busyRequestId === row.requestInfoId ||
          Number(row.assignedToUserId) === Number(currentUserId),
      },
      {
        icon: <PersonAddAlt1Icon fontSize="small" />,
        onClick: (row) =>
          setAssignDialog({ open: true, row, userId: row.assignedToUserId || "" }),
        color: "info",
        tooltip: "ارجاع به کارشناس",
        hidden: (row) => busyRequestId === row.requestInfoId,
      },
    ],
    [openDetails, handleAssign, busyRequestId, currentUserId]
  );

  const handleApplySearch = () => setAppliedSearch(searchText);

  const handleAssignDialogConfirm = async () => {
    const { row, userId } = assignDialog;
    setAssignDialog({ open: false, row: null, userId: "" });
    if (row && userId) await handleAssign(row, Number(userId));
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={PAGE_TITLE}
            subheader="پیگیری متمرکز فرآیند مهرسان پروژه‌ها بر اساس مراحل چهارگانه"
            action={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setImportDialogOpen(true)}
                >
                  آپلود خروجی مهرسان
                </Button>
                <IconButton color="primary" onClick={loadList} aria-label="بروزرسانی">
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />
          <CardContent>
            <Box
              sx={{
                mb: 2,
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleApplySearch()}
                placeholder="جستجو بر اساس نام، موبایل یا کد پیگیری..."
                sx={{ flex: "1 1 260px", minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>مسئول پیگیری</InputLabel>
                <Select
                  label="مسئول پیگیری"
                  value={assigneeFilter}
                  onChange={(e) => setAssigneeFilter(e.target.value)}
                >
                  <MenuItem value="">همه</MenuItem>
                  {currentUserId ? (
                    <MenuItem value={currentUserId}>موارد من</MenuItem>
                  ) : null}
                  {experts
                    .filter((x) => Number(x.id ?? x.Id) !== Number(currentUserId))
                    .map((x) => (
                      <MenuItem key={x.id ?? x.Id} value={x.id ?? x.Id}>
                        {x.fullName ?? x.FullName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <Button variant="contained" onClick={handleApplySearch}>
                اعمال فیلتر
              </Button>

              <Chip size="small" variant="outlined" label={`${rows.length} پروژه`} />
            </Box>

            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
              {QUEUE_TABS.map((tab) => (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  label={
                    <Badge
                      badgeContent={counts[tab.countField]}
                      color={tab.key === "completed" ? "success" : "primary"}
                      max={999}
                      sx={{ "& .MuiBadge-badge": { right: -14 } }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tab.label}
                      </Typography>
                    </Badge>
                  }
                  sx={{ px: 3 }}
                />
              ))}
            </Tabs>

            <EnhancedTable
              rowKey="requestInfoId"
              rows={tableRows}
              headCells={headCells}
              actions={tableActions}
              loading={loading}
            />
          </CardContent>
        </Card>
      </Grid>

      <Dialog
        open={assignDialog.open}
        onClose={() => setAssignDialog({ open: false, row: null, userId: "" })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>ارجاع پیگیری مهرسان</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            متقاضی: {assignDialog.row?.applicantFullName || "—"} (کد{" "}
            {assignDialog.row?.requestTrackingCode || "—"})
          </Typography>
          <FormControl size="small" fullWidth>
            <InputLabel>کارشناس مسئول</InputLabel>
            <Select
              label="کارشناس مسئول"
              value={assignDialog.userId}
              onChange={(e) =>
                setAssignDialog((prev) => ({ ...prev, userId: e.target.value }))
              }
            >
              {experts.map((x) => (
                <MenuItem key={x.id ?? x.Id} value={x.id ?? x.Id}>
                  {x.fullName ?? x.FullName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog({ open: false, row: null, userId: "" })}>
            انصراف
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignDialogConfirm}
            disabled={!assignDialog.userId}
          >
            ثبت ارجاع
          </Button>
        </DialogActions>
      </Dialog>

      <MehrsanWorkQueueCaseDrawer
        open={detailDrawer.open}
        queueRow={detailDrawer.row}
        autoOpenNext={detailDrawer.autoOpenNext}
        onClose={() =>
          setDetailDrawer({ open: false, row: null, autoOpenNext: false })
        }
        onSaved={loadList}
      />

      <MehrsanPortalExcelImportDialog
        open={importDialogOpen}
        source={OPERATIONS_SOURCE}
        onClose={() => setImportDialogOpen(false)}
        onCommitted={loadList}
      />
    </Grid>
  );
};

export default MehrsanWorkQueuePage;
